/**
 * BecomeHostPage Component
 *
 * Landing page for guests to become hosts with benefits explanation.
 */

import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PublicLayout, Container } from '../../components/layout';
import { useAuth } from '../../contexts';

export const BecomeHostPage: React.FC = () => {
  const { user, updateProfile, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Redirect if already a host
  if (!authLoading && user?.role === 'host') {
    return <Navigate to="/host" replace />;
  }

  const handleBecomeHost = async () => {
    if (!acceptedTerms) {
      toast.error('Please accept the Host Terms of Service');
      return;
    }

    setIsUpgrading(true);

    try {
      // Update user role to host
      await updateProfile({ role: 'host' } as any);
      toast.success('Welcome! You are now a host.');
      navigate('/host/listings/new');
    } catch (err) {
      toast.error('Failed to upgrade account. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-primary-100 py-16 lg:py-24">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              List your place. Make it pay for itself.
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              150+ owners across Lebanon already do. Set up takes about an hour;
              your listing goes live after we visit.
            </p>
            {!user && (
              <Link
                to="/login?redirect=/become-host"
                className="inline-block px-8 py-4 bg-primary-500 text-white text-lg font-semibold rounded-xl hover:bg-primary-600 transition-colors"
              >
                Log in to Get Started
              </Link>
            )}
          </div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <Container>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            What you get
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <BenefitCard
              icon={<DollarIcon className="w-10 h-10" />}
              title="A second income"
              description="Hosts in Batroun and Faraya clear $500 to $3,000 a month. You set the rates."
            />
            <BenefitCard
              icon={<ShieldIcon className="w-10 h-10" />}
              title="Paid the day after check-in"
              description="We hold the guest's money, then send yours via Whish or bank transfer 24 hours after they arrive."
            />
            <BenefitCard
              icon={<CalendarIcon className="w-10 h-10" />}
              title="Your calendar, your rules"
              description="Block weekends, set a minimum stay, refuse parties. It's still your home."
            />
            <BenefitCard
              icon={<SupportIcon className="w-10 h-10" />}
              title="A real phone number"
              description="The Beirut team picks up day or night. WhatsApp works too."
            />
            <BenefitCard
              icon={<InsuranceIcon className="w-10 h-10" />}
              title="Damage cover up to $1M"
              description="If a guest breaks something, we file the claim. You don't chase them."
            />
            <BenefitCard
              icon={<GlobeIcon className="w-10 h-10" />}
              title="Guests who actually show up"
              description="We verify ID and hold a deposit before booking. No surprise no-shows."
            />
          </div>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <Container>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How it works
          </h2>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <StepCard
              step={1}
              title="Tell us about the place"
              description="Bedrooms, photos, the price you want. About 20 minutes."
            />
            <StepCard
              step={2}
              title="We come visit"
              description="A team member walks the property and uploads professional photos."
            />
            <StepCard
              step={3}
              title="Open your calendar"
              description="Block out the weekends you want for yourself. The rest is bookable."
            />
            <StepCard
              step={4}
              title="Get paid"
              description="Whish or bank transfer, 24 hours after each guest checks in."
            />
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      {user && (
        <section className="py-16">
          <Container>
            <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Want to host?
              </h2>
              <p className="text-gray-600 mb-6">
                The first listing takes about 20 minutes.
              </p>

              <label className="flex items-start gap-3 text-left mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-600">
                  I agree to the{' '}
                  <Link to="/host-terms" className="text-primary-600 hover:underline">
                    Host Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/host-guidelines" className="text-primary-600 hover:underline">
                    Community Guidelines
                  </Link>
                </span>
              </label>

              <button
                onClick={handleBecomeHost}
                disabled={isUpgrading || !acceptedTerms}
                className="w-full py-4 bg-primary-500 text-white text-lg font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpgrading ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingSpinner />
                    Setting up...
                  </span>
                ) : (
                  'Start Hosting'
                )}
              </button>
            </div>
          </Container>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <Container>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Things hosts ask
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <FAQItem
              question="How much will I make?"
              answer="It depends on the place and the season. A two-bedroom in Batroun pulls $2,000 to $3,500 in summer. A chalet in Faraya does similar numbers in winter. The pricing tool will show you a range for your area."
            />
            <FAQItem
              question="What if a guest damages something?"
              answer="We file the claim. Cover goes up to $1,000,000 per stay. You send us photos; we handle the rest."
            />
            <FAQItem
              question="When does the money hit my account?"
              answer="24 hours after the guest checks in. Pick Whish Money or a bank transfer; you can change it any time."
            />
            <FAQItem
              question="Can I refuse certain guests or set house rules?"
              answer="Yes. Set check-in times, ban parties, require a minimum stay, decline a booking. Your home, your call."
            />
          </div>
        </Container>
      </section>
    </PublicLayout>
  );
};

// Benefit Card
interface BenefitCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const BenefitCard: React.FC<BenefitCardProps> = ({ icon, title, description }) => (
  <div className="text-center p-6">
    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-2xl mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

// Step Card
interface StepCardProps {
  step: number;
  title: string;
  description: string;
}

const StepCard: React.FC<StepCardProps> = ({ step, title, description }) => (
  <div className="text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-500 text-white text-xl font-bold rounded-full mb-4">
      {step}
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

// FAQ Item
interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left"
      >
        <span className="font-semibold text-gray-900">{question}</span>
        <ChevronIcon className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-0">
          <p className="text-gray-600">{answer}</p>
        </div>
      )}
    </div>
  );
};

// Icons
const DollarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const SupportIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const InsuranceIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const GlobeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const LoadingSpinner: React.FC = () => (
  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export default BecomeHostPage;
